/**
 * A screen as the backend's spec declares it (ui-spec.ts), turned into the ScreenDef every module already reads: the
 * spec says what is on the screen, the pack only how to compute what needs computing (its builders, menus and
 * actions, named by role; docs/NEW-UI/05-spec-ui.md).
 *
 * In the props, `{t: key, with: {…}}` is translated, `{session: field}` (a dotted path) reads the session, and with
 * `or:` gives that value when the field is empty (null, missing or ''), `{money: value}` is an amount in the screen's
 * language (no number: nothing); anything else is passed as it is. A screen with `by:` shows the case of `cases:` that
 * the session field's value names. A button in the bar is gated by the action it runs, not by its id (a button may run an
 * action of another name): the backend's `valid_actions` names actions.
 */
import { reportOnce } from '../core/diagnostics';
import { formatMoney } from '../i18n/translator';
import type { ActionDef, DomainPack, KioskPack, ScreenContext, ScreenDef, WidgetCall } from './contract';
import { PLATFORM_STRINGS } from '../i18n/strings';
import { uiDocumentOf, type BarEntry, type ScreenUi, type UiDocument, type ViewUi } from './ui-spec';
import type { LayoutChange } from '../layout/changes';

/** The value with its `{t:}` and `{session:}` resolved, at any depth. */
export function resolveValue(value: unknown, ctx: ScreenContext): unknown {
  if (Array.isArray(value)) return value.map((item) => resolveValue(item, ctx));
  if (value === null || typeof value !== 'object') return value;
  const node = value as Record<string, unknown>;
  if (typeof node.t === 'string' && Object.keys(node).every((k) => k === 't' || k === 'with')) {
    const values = node.with ? (resolveValue(node.with, ctx) as Record<string, string | number>) : undefined;
    return ctx.t(node.t, values);
  }
  if ('money' in node && Object.keys(node).length === 1) {
    const amount = resolveValue(node.money, ctx);
    return typeof amount === 'number' ? formatMoney(amount, ctx.language) : undefined;
  }
  if (typeof node.session === 'string' && Object.keys(node).every((k) => k === 'session' || k === 'or')) {
    const read = readPath(ctx.session, node.session);
    return (read === null || read === undefined || read === '') && 'or' in node ? resolveValue(node.or, ctx) : read;
  }
  return Object.fromEntries(Object.entries(node).map(([k, v]) => [k, resolveValue(v, ctx)]));
}

function readPath(source: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((at, key) => (at && typeof at === 'object' ? (at as Record<string, unknown>)[key] : undefined), source);
}

function barAction(entry: BarEntry, document: UiDocument, pack: DomainPack, ctx: ScreenContext): ActionDef | null {
  if ('menu' in entry) {
    const menu = pack.menus?.[entry.menu];
    if (!menu) reportOnce('warn', 'layout', `the spec names menu "${entry.menu}", which the ${pack.id} pack has not`);
    return menu ?? null;
  }
  if ('action' in entry) {
    const build = pack.actions?.[entry.action];
    if (!build) reportOnce('warn', 'layout', `the spec names action "${entry.action}", which the ${pack.id} pack has not`);
    return build ? build(ctx) : null;
  }
  const button = document.buttons[entry.button];
  return {
    id: entry.button,
    label: entry.label ?? button?.label ?? entry.button,
    icon: entry.icon ?? button?.icon,
    tone: entry.tone ?? button?.tone,
    place: entry.place ?? button?.place,
    press: entry.data ? { button: entry.button, data: resolveValue(entry.data, ctx) as Record<string, unknown> } : { button: entry.button },
    gate: button?.action ?? entry.button,
  };
}

function drawn(view: ViewUi, pack: DomainPack, ctx: ScreenContext): WidgetCall | null {
  const props = (resolveValue(view.props ?? {}, ctx) ?? {}) as Record<string, unknown>;
  if (view.build) {
    const builder = pack.builders?.[view.build];
    if (!builder) {
      reportOnce('warn', 'layout', `the spec names builder "${view.build}", which the ${pack.id} pack has not`);
      return null;
    }
    return builder(ctx, props);
  }
  return view.widget ? { widget: view.widget, props, ...(view.variant ? { variant: view.variant } : {}) } : null;
}

/** The view of a screen: its own, or the case its `by:` field names (none: nothing, said once). */
function viewOf(ui: ScreenUi, ctx: ScreenContext): ViewUi | null {
  if (!ui.cases || !ui.by) return ui;
  const value = String(readPath(ctx.session, ui.by));
  const view = ui.cases[value];
  if (!view) reportOnce('warn', 'layout', `the spec has no case "${value}" of ${ui.by} for screen ${ctx.session.state}`);
  return view ?? null;
}

/** The widgets a screen may show: its view's, or each case's; a builder's is the one it declares. */
function widgetsOf(ui: ScreenUi, pack: DomainPack): string[] {
  const views: ViewUi[] = ui.cases ? Object.values(ui.cases) : [ui];
  const keys = views.map((v) => (v.build ? pack.builders?.[v.build]?.widget : v.widget)).filter((k): k is string => Boolean(k));
  return [...new Set(keys)];
}

/** The ScreenDef of one described screen. */
export function specScreen(ui: ScreenUi, document: UiDocument, pack: DomainPack): ScreenDef {
  const def: ScreenDef = {
    view(ctx): WidgetCall | null {
      const view = viewOf(ui, ctx);
      return view ? drawn(view, pack, ctx) : null;
    },
    actions: (ctx) => (ui.bar ?? []).map((entry) => barAction(entry, document, pack, ctx)).filter((a): a is ActionDef => a !== null),
  };
  if (ui.hint) def.hint = ui.hint;
  if (ui.step) def.step = ui.step;
  const widgets = widgetsOf(ui, pack);
  if (widgets.length) def.widgets = widgets;
  if (ui.layout?.length) def.layout = ui.layout as LayoutChange[];
  return def;
}

/**
 * The pack the kiosk runs: the pack with its spec's screens, steps and words. A document without words (older than
 * the words in the spec) takes the pack's bundled one's. A document of another domain, or none, gives no screen: each
 * one is a visible gap (screen.ts `screenOf`), never another domain's screen.
 */
export function withSpec(pack: DomainPack, document: UiDocument | null): KioskPack {
  if (!document || document.domain !== pack.id) {
    if (document) reportOnce('warn', 'layout', `the screens' spec is of "${document.domain}", the pack is ${pack.id}: no screen is shown`);
    return { ...pack, screens: {}, steps: [], strings: {}, languages: [], buttons: {} };
  }
  const screens = Object.fromEntries(Object.entries(document.screens).map(([name, ui]) => [name, specScreen(ui, document, pack)]));
  const strings = Object.keys(document.strings).length > 0 ? document.strings : uiDocumentOf(pack.ui)?.strings ?? {};
  const languages = Object.keys(PLATFORM_STRINGS).filter((language) => language in strings);
  const buttons = Object.fromEntries(Object.entries(document.buttons).map(([id, button]) => [id, button.action]));
  return { ...pack, screens, steps: document.steps, strings, languages, buttons };
}
